import React, { useEffect, useRef } from 'react';

const ChatMessages = ({ 
  messages, 
  userInfo, 
  userType, 
  hiringData, 
  onAcceptHiring, 
  onRejectHiring, 
  isProcessingHiring 
}) => {
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const isMessageFromCurrentUser = (message) => {
    if (!userInfo) return false;
    
    const isProfessional = userType === 'profesionales';
    
    if (isProfessional) {
      // For professionals, check if message was sent by employee
      return message.senderEmployeeId === userInfo.employeeId || 
             message.senderType === 'employee';
    } else {
      // For companies, check if message was sent by restaurant user
      return message.senderRestaurantUserId === userInfo.restaurantUserId ||
             (message.senderRestaurantUserId === userInfo.userId && !userInfo.restaurantUserId) ||
             message.senderType === 'restaurant';
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleString('es-ES', { 
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  if (!messages || messages.length === 0) {
    return (
      <div className="chat__messages">
        <div style={{ 
          textAlign: 'center', 
          padding: '20px', 
          color: '#999',
          fontStyle: 'italic' 
        }}>
          No hay mensajes aún. ¡Comienza la conversación!
        </div>
        <div ref={messagesEndRef} />
      </div>
    );
  }

  // Check if a message is a hiring offer message
  const isHiringOfferMessage = (messageText) => {
    return messageText && messageText.includes('Te han intentado contratar');
  };

  // Find the most recent hiring offer message index
  const findMostRecentHiringOfferIndex = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const messageText = messages[i].text || messages[i].content || '';
      if (isHiringOfferMessage(messageText) && !isMessageFromCurrentUser(messages[i])) {
        return i;
      }
    }
    return -1;
  };

  const mostRecentHiringOfferIndex = findMostRecentHiringOfferIndex();

  // Check if hiring offer can be accepted/rejected (only for the most recent one)
  const canRespondToHiring = (messageText, messageIndex) => {
    // Must be a hiring offer message, the most recent one, and user must be an employee
    if (!isHiringOfferMessage(messageText) || 
        messageIndex !== mostRecentHiringOfferIndex ||
        userType !== 'profesionales') {
      return false;
    }
    
    // If hiringData exists, only show buttons if status is 'offered'
    if (hiringData) {
      return hiringData.status === 'offered';
    }
    
    // If hiringData is null but this is the most recent offer message, show buttons
    // (hiringData might be loading or not yet fetched)
    return true;
  };

  // Check if there's a rejection/acceptance message after a hiring offer message
  const getHiringStatusFromMessages = (hiringOfferIndex) => {
    // Look for rejection or acceptance messages after this hiring offer
    for (let i = hiringOfferIndex + 1; i < messages.length; i++) {
      const msgText = messages[i].text || messages[i].content || '';
      const isFromEmployee = isMessageFromCurrentUser(messages[i]);
      
      if (isFromEmployee) {
        if (msgText.includes('rechazado') || msgText.includes('rechazé')) {
          return '❌ Rechazado';
        }
        if (msgText.includes('aceptado') || msgText.includes('acepté')) {
          return '✅ Aceptado';
        }
      }
    }
    
    // If this is not the most recent offer and hiringData shows rejected/accepted, use that
    if (hiringOfferIndex !== mostRecentHiringOfferIndex && hiringData) {
      if (hiringData.status === 'rejected') {
        return '❌ Rechazado';
      }
      if (hiringData.status === 'accepted' || hiringData.status === 'active') {
        return '✅ Aceptado';
      }
    }
    
    return null;
  };

  // Get status text for old hiring offer messages
  const getHiringStatusText = (messageIndex) => {
    if (messageIndex === mostRecentHiringOfferIndex) {
      // This is the current one, status is handled by buttons (if status is 'offered')
      // But if it's already rejected/accepted, show status instead
      if (hiringData && hiringData.status !== 'offered') {
        if (hiringData.status === 'rejected') {
          return '❌ Rechazado';
        }
        if (hiringData.status === 'accepted' || hiringData.status === 'active') {
          return '✅ Aceptado';
        }
      }
      return null;
    }
    
    // For older messages, check subsequent messages for status
    return getHiringStatusFromMessages(messageIndex);
  };

  return (
    <div className="chat__messages">
      {messages.map((message, index) => {
        const isSent = isMessageFromCurrentUser(message);
        const messageText = message.text || message.content || '';
        const timestamp = message.createdAt || message.timestamp;
        const isHiringOffer = isHiringOfferMessage(messageText) && !isSent;
        const showHiringButtons = canRespondToHiring(messageText, index);
        const statusText = isHiringOffer ? getHiringStatusText(index) : null;

        return (
          <div 
            key={message.id || index} 
            className={`chat__message ${isSent ? 'sent' : 'received'}`}
          >
            <div className="message-content">
              <div className="message-text">{messageText}</div>
              {showHiringButtons && (
                <div className="hiring-offer-buttons" style={{
                  marginTop: '0.75rem',
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap'
                }}>
                  <button
                    onClick={onAcceptHiring}
                    disabled={isProcessingHiring}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: isProcessingHiring ? 'not-allowed' : 'pointer',
                      opacity: isProcessingHiring ? 0.6 : 1
                    }}
                  >
                    ✅ Aceptar
                  </button>
                  <button
                    onClick={onRejectHiring}
                    disabled={isProcessingHiring}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#f44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: isProcessingHiring ? 'not-allowed' : 'pointer',
                      opacity: isProcessingHiring ? 0.6 : 1
                    }}
                  >
                    ❌ Rechazar
                  </button>
                </div>
              )}
              {statusText && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.5rem 1rem',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#666',
                  display: 'inline-block'
                }}>
                  {statusText}
                </div>
              )}
              {timestamp && (
                <div className="message-timestamp">
                  {formatTimestamp(timestamp)}
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessages;

